import React, { useState, useMemo, memo, useRef, useCallback } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { StatusBadge, TypeBadge, getRowStatusClass } from './StatusBadge';
import { 
  ChevronUp, 
  ChevronDown, 
  Search, 
  MoreHorizontal,
  Link2Off,
  Trash2,
  Edit,
  Check,
  X,
  CheckSquare,
  Square,
  Tag,
  CheckCircle,
  AlertTriangle,
  Filter,
  MapPin,
  Eye,
  EyeOff,
  Pencil,
  Link as LinkIcon,
  Copy,
  FileText
} from 'lucide-react';
import { PdfCell, BulkPdfButton, BilletFilterHeader, matchBilletFilter } from './TicketPdfActions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { findStadium } from '../../data/stadiums';
import StadiumPlanModal from './StadiumPlanModal';

// Sort icon component - extracted to avoid nested component issue
const SortIcon = ({ columnKey, sortConfig }) => {
  if (sortConfig.key !== columnKey) return null;
  return sortConfig.direction === 'asc' 
    ? <ChevronUp className="h-3 w-3 ml-1" />
    : <ChevronDown className="h-3 w-3 ml-1" />;
};

// Completely isolated column filter component to prevent re-render loops
// Uses local state for selections and only commits to parent on close
// NOW: Uses pre-loaded filter values from backend for complete list
const ColumnFilterDropdown = memo(function ColumnFilterDropdown({
  columnKey,
  tickets,
  columnFilters,
  otherFiltersTickets, // Tickets filtered by OTHER columns (not this one)
  allFilterValues, // NEW: Pre-loaded values from backend
  onFilterChange
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  // Local state to track selections without causing parent re-renders
  const [localSelected, setLocalSelected] = useState([]);
  
  // Special constant for empty values
  const EMPTY_VALUE_MARKER = '__VIDE__';
  
  // Use backend values if available, otherwise fall back to tickets
  const uniqueValues = useMemo(() => {
    let values = [];
    let hasEmptyValues = false;
    
    // PRIORITY 1: Use pre-loaded backend values (complete list)
    if (allFilterValues && allFilterValues[columnKey] && allFilterValues[columnKey].length > 0) {
      values = [...allFilterValues[columnKey]];
    } else {
      // FALLBACK: Use otherFiltersTickets if available, otherwise all tickets
      const sourceTickets = otherFiltersTickets || tickets;
      const valuesSet = new Set();
      sourceTickets.forEach(t => {
        const val = t[columnKey];
        if (val !== undefined && val !== null && val !== '') {
          valuesSet.add(String(val).substring(0, 100));
        } else {
          hasEmptyValues = true;
        }
      });
      values = Array.from(valuesSet).sort();
    }
    
    // Check if there are empty values in the dataset
    const sourceTickets = otherFiltersTickets || tickets;
    hasEmptyValues = sourceTickets.some(t => {
      const val = t[columnKey];
      return val === undefined || val === null || val === '';
    });
    
    // Add "(Vide)" option at the beginning if there are empty values
    if (hasEmptyValues) {
      return [EMPTY_VALUE_MARKER, ...values];
    }
    
    return values;
  }, [allFilterValues, otherFiltersTickets, tickets, columnKey]);
  
  // Filter values based on search
  const filteredValues = useMemo(() => {
    if (!searchValue) return uniqueValues;
    const term = searchValue.toLowerCase();
    return uniqueValues.filter(v => String(v).toLowerCase().includes(term));
  }, [uniqueValues, searchValue]);
  
  // Sync local state with parent when dropdown opens
  const handleOpenChange = (open) => {
    if (open) {
      // When opening, sync local state with parent state
      setLocalSelected(columnFilters[columnKey] || []);
      setSearchValue('');
    } else {
      // When closing, commit local changes to parent
      const hasChanges = JSON.stringify(localSelected.sort()) !== JSON.stringify((columnFilters[columnKey] || []).sort());
      if (hasChanges) {
        onFilterChange(columnKey, localSelected.length > 0 ? localSelected : undefined);
      }
    }
    setIsOpen(open);
  };
  
  // Toggle value in local state (no parent update)
  const toggleLocalValue = (value) => {
    setLocalSelected(prev => {
      const isSelected = prev.includes(value);
      if (isSelected) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };
  
  // Clear filter (commit immediately)
  const handleClear = () => {
    setLocalSelected([]);
    onFilterChange(columnKey, undefined);
    setIsOpen(false);
  };
  
  const selectedCount = columnFilters[columnKey]?.length || 0;
  
  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button className={cn(
          "p-0.5 rounded hover:bg-zinc-700 transition-colors flex items-center gap-0.5",
          selectedCount > 0 && "text-[#C4A052]"
        )}>
          <Filter className="h-3 w-3" />
          {selectedCount > 0 && (
            <span className="text-[10px] bg-[#C4A052] text-black rounded-full px-1 font-bold">
              {selectedCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="start" 
        className="w-64 p-0" 
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={() => handleOpenChange(false)}
      >
        {/* Search input */}
        <div className="sticky top-0 bg-zinc-800 z-10 p-2 border-b border-zinc-700">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-500" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full h-7 pl-7 pr-2 text-xs bg-zinc-700 border border-zinc-600 rounded text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#C4A052]"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              autoComplete="off"
            />
          </div>
        </div>
        
        {/* Clear filter option */}
        <DropdownMenuItem 
          onSelect={(e) => {
            e.preventDefault();
            handleClear();
          }}
          className={cn(localSelected.length === 0 && "bg-zinc-700")}
        >
          <X className="h-3 w-3 mr-2" />
          <span className="text-zinc-300">Effacer les filtres</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        
        {/* Values list */}
        <div className="max-h-64 overflow-y-auto">
          {filteredValues.map((value, idx) => {
            const isChecked = localSelected.includes(value);
            const displayValue = value === EMPTY_VALUE_MARKER ? '(Vide)' : (value || '(vide)');
            return (
              <DropdownMenuCheckboxItem 
                key={`${columnKey}-${idx}-${value}`}
                checked={isChecked}
                onCheckedChange={() => toggleLocalValue(value)}
                onSelect={(e) => e.preventDefault()}
                className={cn("text-xs cursor-pointer", value === EMPTY_VALUE_MARKER && "text-zinc-400 italic")}
              >
                <span className="truncate max-w-48">{displayValue}</span>
              </DropdownMenuCheckboxItem>
            );
          })}
          {filteredValues.length === 0 && (
            <div className="p-2 text-xs text-zinc-500 text-center">Aucun résultat</div>
          )}
        </div>
        
        {/* Apply button */}
        {localSelected.length > 0 && (
          <div className="border-t border-zinc-700 p-2">
            <button
              onClick={() => handleOpenChange(false)}
              className="w-full h-7 text-xs bg-[#C4A052] text-black rounded font-semibold hover:bg-[#D4B062] transition-colors"
            >
              Appliquer ({localSelected.length} sélectionné{localSelected.length > 1 ? 's' : ''})
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

// Format event name: Normalize match number display
// "M76 -( ROUND OF 32 )" → "M76 - Round of 32"
// "1C vs 2F - World Cup - Round of M32 (Match M76)" → "M76 - 1C vs 2F - Round of 32"
const formatEventName = (eventName) => {
  if (!eventName) return '-';
  
  // Extract match number
  const matchNumMatch = eventName.match(/\bM?(\d{1,3})\b/i);
  const matchNum = matchNumMatch ? parseInt(matchNumMatch[1], 10) : null;
  
  // Clean up and normalize the name
  let cleaned = eventName
    .replace(/\s+/g, ' ')  // Multiple spaces to single
    .replace(/\(\s*Match\s*M?\d+\s*\)/gi, '')  // Remove "(Match M76)"
    .replace(/\bM?0+(\d)/gi, 'M$1')  // M002 → M2
    .replace(/\.\s*WC\s*$/i, ' - World Cup')  // .WC → - World Cup
    .replace(/-\s*\(\s*/g, ' - ')  // "-( " → " - "
    .replace(/\s*\)\s*/g, ' ')  // " ) " → " "
    .trim();
  
  // If match number found, ensure it's at the start for consistency
  if (matchNum && !cleaned.match(/^M\d/i)) {
    // Remove match number from wherever it is
    cleaned = cleaned.replace(/\s*[-·]\s*M\d+\s*/gi, ' ').trim();
    cleaned = `M${matchNum} - ${cleaned}`;
  }
  
  return cleaned;
};

// Extract match number for sorting  
const extractMatchNum = (eventName) => {
  if (!eventName) return 9999;
  const match = eventName.match(/\bM?(\d{1,3})\b/i);
  return match ? parseInt(match[1], 10) : 9999;
};

// Colonnes pour ACHAT - BASE
const ACHAT_COLUMNS_BASE = [
  { key: 'select', label: '', width: '40px' },
  { key: 'ref_achat', label: 'REF ACHAT', width: '110px' },
  { key: 'evenement', label: 'MATCH', width: '220px' },
  { key: 'categorie', label: 'CATÉGORIE', width: '100px' },
  { key: 'email_compte', label: 'E-MAIL DU COMPTE', width: '180px' },
  { key: 'pw_compte', label: 'PW DU COMPTE', width: '120px' },
  { key: 'source', label: 'SOURCE', width: '100px' },
  { key: 'plan', label: 'PLAN', width: '70px' },
  { key: 'ref_vente', label: 'REF DE VENTE', width: '110px' },
  { key: 'nom_client', label: 'NOM CLIENT', width: '130px' },
  { key: 'mail_client', label: 'MAIL CLIENT', width: '180px' },
  { key: 'actions', label: '', width: '50px' },
];

// Colonnes ROLAND GARROS spécifiques (après MATCH)
const RG_EXTRA_COLUMNS = [
  { key: 'session', label: 'SESSION', width: '80px' },
  { key: 'tribune', label: 'TRIBUNE', width: '100px' },
  { key: 'acces', label: 'ACCÈS', width: '80px' },
  { key: 'escalier', label: 'ESCALIER', width: '80px' },
  { key: 'rang', label: 'RANG', width: '70px' },
  { key: 'place', label: 'SEAT', width: '70px' },
];

// Emplacement + billet PDF (projets hors Roland Garros)
const SEAT_COLUMNS = [
  { key: 'block', label: 'PORTE / BLOC', width: '100px' },
  { key: 'rang', label: 'RANG', width: '60px' },
  { key: 'place', label: 'PLACE', width: '60px' },
];
const PDF_COLUMN = { key: 'pdf', label: 'BILLET', width: '80px' };
const DATE_ACHAT_COLUMN = { key: 'date_achat', label: 'ACHETÉ LE', width: '90px' };

const withSeatColumns = (baseCols, isRG) => {
  const cols = [...baseCols];
  const catIdx = cols.findIndex(c => c.key === 'categorie');
  cols.splice(catIdx + 1, 0, ...(isRG ? [] : SEAT_COLUMNS), PDF_COLUMN);
  const dateIdx = cols.findIndex(c => c.key === 'date_evenement');
  cols.splice(dateIdx + 1, 0, DATE_ACHAT_COLUMN);
  return cols;
};

// Fonction pour obtenir les colonnes ACHAT selon le projet
const getAchatColumns = (project) => {
  const isRG = project && (project.toUpperCase().includes('ROLAND') || project.toUpperCase().includes('RG'));
  if (isRG) {
    // Insérer les colonnes RG après 'evenement'
    const cols = [...ACHAT_COLUMNS_BASE];
    const eventIdx = cols.findIndex(c => c.key === 'evenement');
    cols.splice(eventIdx + 1, 0, ...RG_EXTRA_COLUMNS);
    return withSeatColumns(cols, true);
  }
  return withSeatColumns(ACHAT_COLUMNS_BASE, false);
};

// Colonnes pour VENTE - BASE (sans colonnes RG)
const VENTE_COLUMNS_BASE = [
  { key: 'select', label: '', width: '40px' },
  { key: 'ref_vente', label: 'REF DE VENTE', width: '110px' },
  { key: 'date_operation', label: 'VENDU LE', width: '90px' },
  { key: 'source', label: 'PLATEFORME', width: '100px' },
  { key: 'evenement', label: 'MATCH', width: '220px' },
  { key: 'lieu', label: 'LIEU', width: '150px' },
  { key: 'plan', label: 'PLAN', width: '70px' },
  { key: 'ville', label: 'VILLE', width: '100px' },
  { key: 'pays', label: 'PAYS', width: '80px' },
  { key: 'date_evenement', label: 'DATE', width: '90px' },
  { key: 'categorie', label: 'CATÉGORIE', width: '100px' },
  { key: 'qty', label: 'QTY', width: '50px' },
  { key: 'prix', label: 'PRIX', width: '90px' },
  { key: 'note', label: 'NOTE', width: '100px' },
  { key: 'nom_client', label: 'NOM CLIENT', width: '130px' },
  { key: 'mail_client', label: 'MAIL CLIENT', width: '180px' },
  { key: 'linked_mail_du_compte', label: 'MAIL COMPTE (ACHAT)', width: '180px' },
  { key: 'linked_pw_du_compte', label: 'PW COMPTE (ACHAT)', width: '120px' },
  { key: 'linked_source_achat', label: 'SOURCE ACHAT', width: '100px' },
  { key: 'attribuer', label: 'ATTRIBUER', width: '100px' },
  { key: 'actions', label: '', width: '50px' },
];

// Fonction pour obtenir les colonnes VENTE selon le projet
const getVenteColumns = (project) => {
  const isRG = project && (project.toUpperCase().includes('ROLAND') || project.toUpperCase().includes('RG'));
  if (isRG) {
    const cols = [...VENTE_COLUMNS_BASE];
    const eventIdx = cols.findIndex(c => c.key === 'evenement');
    cols.splice(eventIdx + 1, 0, ...RG_EXTRA_COLUMNS);
    return cols;
  }
  return VENTE_COLUMNS_BASE;
};

// Colonnes pour TOUT - BASE (sans colonnes RG)
const TOUT_COLUMNS_BASE = [
  { key: 'select', label: '', width: '40px' },
  { key: 'type', label: 'TYPE', width: '70px' },
  { key: 'ref_achat', label: 'REF ACHAT', width: '110px' },
  { key: 'evenement', label: 'MATCH', width: '200px' },
  { key: 'categorie', label: 'CATÉGORIE', width: '100px' },
  { key: 'date_evenement', label: 'DATE', width: '90px' },
  { key: 'qty', label: 'QTY', width: '50px' },
  { key: 'prix', label: 'PRIX', width: '90px' },
  { key: 'source', label: 'SOURCE', width: '100px' },
  { key: 'ref_vente', label: 'REF DE VENTE', width: '110px' },
  { key: 'date_operation', label: 'VENDU LE', width: '90px' },
  { key: 'lieu', label: 'LIEU', width: '130px' },
  { key: 'plan', label: 'PLAN', width: '70px' },
  { key: 'ville', label: 'VILLE', width: '90px' },
  { key: 'pays', label: 'PAYS', width: '70px' },
  { key: 'note', label: 'NOTE', width: '80px' },
  { key: 'email_compte', label: 'E-MAIL COMPTE', width: '160px' },
  { key: 'pw_compte', label: 'PW COMPTE', width: '100px' },
  { key: 'nom_client', label: 'NOM CLIENT', width: '120px' },
  { key: 'mail_client', label: 'MAIL CLIENT', width: '160px' },
  { key: 'attribuer', label: 'ATTRIBUER', width: '100px' },
  { key: 'actions', label: '', width: '50px' },
];

// Fonction pour obtenir les colonnes TOUT selon le projet
const getToutColumns = (project) => {
  const isRG = project && (project.toUpperCase().includes('ROLAND') || project.toUpperCase().includes('RG'));
  if (isRG) {
    const cols = [...TOUT_COLUMNS_BASE];
    const eventIdx = cols.findIndex(c => c.key === 'evenement');
    cols.splice(eventIdx + 1, 0, ...RG_EXTRA_COLUMNS);
    return withSeatColumns(cols, true);
  }
  return withSeatColumns(TOUT_COLUMNS_BASE, false);
};

// Composant de récapitulatif par match quand un filtre est actif
const FilterSummaryPanel = ({ tickets, hasFilter, searchTerm, onMatchClick, selectedMatchFilter, onClearMatchFilter }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Calculer les stats par match - TOUJOURS appelé (règle des hooks)
  const matchStats = useMemo(() => {
    if (!hasFilter || tickets.length === 0) {
      return { matches: [], grandTotalQty: 0, grandTotalPrice: 0, grandTotalAttribue: 0, grandTotalNonAttribue: 0 };
    }
    
    // EXCLURE les tickets ANNULÉS et les VENTE du récapitulatif
    // Le récap ne doit compter que les ACHAT (les VENTE sont la contrepartie, pas des billets en plus)
    const activeTickets = tickets.filter(t => 
      t.statut !== 'ANNULEE' && t.statut !== 'ANNULÉ' && t.type === 'ACHAT'
    );
    
    // Récupérer les prix de vente pour calculer le total financier
    const venteTickets = tickets.filter(t => 
      t.type === 'VENTE' && t.statut !== 'ANNULEE' && t.statut !== 'ANNULÉ'
    );
    // Map ref_vente → prix de vente
    const ventePriceMap = {};
    venteTickets.forEach(v => {
      const ref = v.ref_vente;
      if (ref && ref !== '' && ref !== '-' && ref !== 'N/A') {
        ventePriceMap[ref] = (ventePriceMap[ref] || 0) + (parseFloat(v.prix) || 0);
      }
    });
    
    const stats = {};
    let grandTotalQty = 0;
    let grandTotalPrice = 0;
    let grandTotalAttribue = 0;
    let grandTotalNonAttribue = 0;
    
    activeTickets.forEach(ticket => {
      // Extraire le numéro de match
      const matchNumMatch = ticket.evenement?.match(/M(\d+)/i);
      const matchKey = matchNumMatch ? `M${matchNumMatch[1]}` : 'Autre';
      
      if (!stats[matchKey]) {
        stats[matchKey] = {
          matchNum: matchKey,
          eventName: ticket.evenement,
          totalQty: 0,
          totalPrice: 0,
          totalAttribue: 0,
          totalNonAttribue: 0,
          categories: {}
        };
      }
      
      const qty = parseInt(ticket.qty) || 1;
      const price = parseFloat(ticket.prix) || 0;
      // Si le billet a un ref_vente, prendre le prix de vente correspondant
      const refVente = ticket.ref_vente;
      const hasRefVente = refVente && refVente !== '' && refVente !== '-' && refVente !== 'N/A';
      const ventePrice = hasRefVente ? (ventePriceMap[refVente] || 0) : 0;
      const displayPrice = ventePrice > 0 ? ventePrice : price;
      // Détection attribution: a une ref_vente OU attribue=true OU statut MATCH_OK/SPECIAL_MATCH
      const isAttribue = ticket.attribue === true || hasRefVente || ticket.statut === 'MATCH_OK' || ticket.statut === 'SPECIAL_MATCH';
      
      stats[matchKey].totalQty += qty;
      stats[matchKey].totalPrice += displayPrice;
      grandTotalQty += qty;
      grandTotalPrice += displayPrice;
      
      // Compteurs attribués/non attribués
      if (isAttribue) {
        stats[matchKey].totalAttribue += qty;
        grandTotalAttribue += qty;
      } else {
        stats[matchKey].totalNonAttribue += qty;
        grandTotalNonAttribue += qty;
      }
      
      // Stats par catégorie
      const cat = ticket.categorie || 'N/A';
      if (!stats[matchKey].categories[cat]) {
        stats[matchKey].categories[cat] = { qty: 0, price: 0, attribue: 0, nonAttribue: 0 };
      }
      stats[matchKey].categories[cat].qty += qty;
      stats[matchKey].categories[cat].price += displayPrice;
      if (isAttribue) {
        stats[matchKey].categories[cat].attribue += qty;
      } else {
        stats[matchKey].categories[cat].nonAttribue += qty;
      }
    });
    
    // Trier par numéro de match
    const sortedMatches = Object.values(stats).sort((a, b) => {
      const numA = parseInt(a.matchNum.replace('M', '')) || 999;
      const numB = parseInt(b.matchNum.replace('M', '')) || 999;
      return numA - numB;
    });
    
    return { matches: sortedMatches, grandTotalQty, grandTotalPrice, grandTotalAttribue, grandTotalNonAttribue };
  }, [tickets, hasFilter]);
  
  // Ne rien afficher si pas de filtre actif ou pas de résultats
  if (!hasFilter || tickets.length === 0 || matchStats.matches.length === 0) {
    return null;
  }
  
  return (
    <div className="bg-zinc-800/50 border border-[#C4A052]/30 rounded-lg p-4 mb-4" data-testid="filter-summary-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[#C4A052] flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Récapitulatif {searchTerm && <span className="text-zinc-400">pour "{searchTerm}"</span>}
          </h3>
          {/* Bouton Masquer/Afficher */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-xs text-zinc-400 hover:text-[#C4A052] transition-colors flex items-center gap-1 ml-2"
          >
            {isCollapsed ? (
              <>
                <ChevronDown className="h-3 w-3" />
                Afficher
              </>
            ) : (
              <>
                <ChevronUp className="h-3 w-3" />
                Masquer
              </>
            )}
          </button>
          {/* Bouton Retour si un match est sélectionné */}
          {selectedMatchFilter && (
            <button
              onClick={onClearMatchFilter}
              className="text-xs bg-[#C4A052]/20 text-[#C4A052] px-2 py-1 rounded hover:bg-[#C4A052]/30 transition-colors flex items-center gap-1 ml-2"
            >
              <X className="h-3 w-3" />
              Retour (tous les matchs)
            </button>
          )}
        </div>
        <div className="text-sm font-bold text-white bg-[#C4A052]/20 px-3 py-1 rounded flex items-center gap-3">
          <span>TOTAL: {matchStats.grandTotalQty} billets</span>
          <span className="text-green-400">{matchStats.grandTotalAttribue} attrib.</span>
          <span className="text-orange-400">RESTE {matchStats.grandTotalNonAttribue}</span>
        </div>
      </div>
      
      {/* Contenu collapsible */}
      {!isCollapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {matchStats.matches.map(match => (
            <div 
              key={match.matchNum}
              onClick={() => onMatchClick?.(match.matchNum)}
              className={cn(
                "bg-zinc-900/80 border rounded-lg p-3 cursor-pointer transition-all hover:border-[#C4A052]/50 hover:bg-zinc-800/50",
                selectedMatchFilter === match.matchNum 
                  ? "border-[#C4A052] ring-1 ring-[#C4A052]/30" 
                  : "border-zinc-700"
              )}
            >
              {/* Header du match */}
              <div className="flex items-center justify-between mb-2">
                <span className={cn(
                  "font-bold px-2 py-0.5 rounded text-sm",
                  selectedMatchFilter === match.matchNum 
                    ? "bg-[#C4A052] text-black" 
                    : "bg-[#C4A052] text-black"
                )}>
                  {match.matchNum}
                </span>
                <span className="text-white font-semibold">
                  {match.totalQty} <span className="text-zinc-400 font-normal text-xs">billets</span>
                </span>
              </div>
              
              {/* Compteurs Attribués / Reste */}
              <div className="flex items-center justify-between text-xs mb-2 pb-2 border-b border-zinc-700">
                <div className="flex items-center gap-1">
                  <span className="text-green-400 font-bold">{match.totalAttribue}</span>
                  <span className="text-zinc-500">attribués</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-zinc-500">RESTE</span>
                  <span className={cn(
                    "font-bold",
                    match.totalNonAttribue === 0 ? "text-green-400" : "text-orange-400"
                  )}>{match.totalNonAttribue}</span>
                </div>
              </div>
              
              {/* Détail par catégorie */}
              <div className="space-y-1">
                {Object.entries(match.categories)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([cat, data]) => (
                  <div key={cat} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-400">{cat}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">{data.attribue}</span>
                      <span className="text-zinc-500">/</span>
                      <span className="text-white font-medium">{data.qty}</span>
                      {data.nonAttribue > 0 && (
                        <span className="text-orange-400 text-[10px]">R:{data.nonAttribue}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Prix total du match */}
              <div className="mt-2 pt-2 border-t border-zinc-700 flex justify-between text-xs">
                <span className="text-zinc-500">Total</span>
                <span className="text-[#C4A052] font-semibold">{match.totalPrice.toFixed(2)} €</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const TicketTable = ({ 
  tickets = [], 
  onUnmatch, 
  onDelete, 
  onBulkDelete,
  onEdit,
  onUpdateStatus,
  onMarkAttribue,
  onValidateTicket,  // NEW: callback to validate ticket (green)
  onAttribuerVente,  // NEW: callback for VENTE attribution modal
  onOpenProofModal,  // NEW: callback to open proof upload modal (VENTE)
  onOpenProofEnvoiModal,  // NEW: callback to open proof envoi modal (ACHAT)
  loading = false,
  viewType = 'tout', // 'tout', 'achat', 'vente'
  globalSearchTerm = '',  // Global search term from parent
  onSearchTermChange,     // Callback to update global search term
  stats = null,           // Stats object with total counts
  isLightMode = false,    // Light mode flag - don't apply Tailwind bg classes in light mode
  isSilentRefresh = false,  // When true, don't reset pagination/filters (for auto-refresh)
  onFilteredTicketsChange,  // Callback to send filtered tickets to parent (for export)
  allFilterValues = null,   // NEW: Pre-loaded filter values from backend
  project = 'WORLD CUP 2026',  // Current project for conditional columns
}) => {
  // Use global search term if provided, otherwise use local state
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const searchTerm = globalSearchTerm !== undefined && onSearchTermChange ? globalSearchTerm : localSearchTerm;
  const setSearchTerm = onSearchTermChange || setLocalSearchTerm;
  
  const [sortConfig, setSortConfig] = useState({ key: 'evenement', direction: 'asc' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const PAGE_OPTIONS = [100, 500, 1000, 2000, 5000];
  
  // Stadium plan modal state
  const [stadiumPlanOpen, setStadiumPlanOpen] = useState(false);
  const [selectedStadium, setSelectedStadium] = useState(null);
  const [selectedMatchInfo, setSelectedMatchInfo] = useState(null);
  
  const onShowStadiumPlan = (stadium, matchInfo = null) => {
    setSelectedStadium(stadium);
    setSelectedMatchInfo(matchInfo);
    setStadiumPlanOpen(true);
  };
  
  // Status filter state
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'attribue', 'non_attribue', 'valide', 'special'
  const [billetFilter, setBilletFilter] = useState('all');
  
  // Match filter from recap panel click
  const [recapMatchFilter, setRecapMatchFilter] = useState(null);
  
  // Column visibility state
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  
  // Column filters state - stores filter value(s) for each column (supports multi-select as array)
  const [columnFilters, setColumnFilters] = useState({});
  
  // Bulk edit modal state
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditField, setBulkEditField] = useState('');
  const [bulkEditValue, setBulkEditValue] = useState('');
  
  // Fields available for bulk edit
  const BULK_EDIT_FIELDS = [
    { key: 'evenement', label: 'Match / Événement' },
    { key: 'categorie', label: 'Catégorie' },
    { key: 'source', label: 'Source / Plateforme' },
    { key: 'lieu', label: 'Lieu' },
    { key: 'ville', label: 'Ville' },
    { key: 'pays', label: 'Pays' },
    { key: 'date_evenement', label: 'Date événement' },
    { key: 'nom_client', label: 'Nom client' },
    { key: 'mail_client', label: 'Email client' },
    { key: 'email_compte', label: 'E-mail Compte' },
    { key: 'pw_compte', label: 'PW Compte' },
    { key: 'statut', label: 'Statut' },
    { key: 'note', label: 'Note' },
    { key: 'prix', label: 'Prix' },
    { key: 'qty', label: 'Quantité' },
    { key: 'block', label: 'Block' },
    { key: 'rang', label: 'Rang' },
    { key: 'place', label: 'Place' },
    { key: 'mode_livraison', label: 'Mode livraison' },
  ];
  
  // Helper: Get tickets filtered by all column filters EXCEPT the specified one
  // This enables cascading filters - show only relevant values based on other active filters
  const getTicketsFilteredExcept = React.useCallback((excludeColumnKey) => {
    let result = [...tickets];
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(ticket => {
        const isVente = ticket.type === 'VENTE';
        const isAttribue = isVente 
          ? ticket.attribue === true
          : (ticket.attribue === true || (ticket.ref_vente && ticket.ref_vente.trim() !== ''));
        const isValide = ticket.statut === 'VALIDE';
        const isSpecial = ticket.statut === 'SPECIAL_MATCH';
        
        switch (statusFilter) {
          case 'attribue':
            return isAttribue && !isValide && !isSpecial;
          case 'non_attribue':
            return !isAttribue && !isValide;
          case 'valide':
            return isValide;
          case 'special':
            return isSpecial;
          default:
            return true;
        }
      });
    }
    
    // Special marker for empty values in filters
    const EMPTY_VALUE_MARKER = '__VIDE__';
    
    // Apply all column filters EXCEPT the excluded one
    Object.entries(columnFilters).forEach(([columnKey, filterValue]) => {
      if (columnKey === excludeColumnKey) return; // Skip the excluded column
      if (filterValue !== undefined && filterValue !== null) {
        if (Array.isArray(filterValue) && filterValue.length === 0) return;
        
        result = result.filter(ticket => {
          const ticketValue = ticket[columnKey];
          const ticketValueStr = String(ticketValue || '').toLowerCase();
          
          if (Array.isArray(filterValue)) {
            return filterValue.some(fv => {
              // Handle "(Vide)" filter - match empty, null, or undefined values
              if (fv === EMPTY_VALUE_MARKER) {
                return ticketValue === undefined || ticketValue === null || ticketValue === '';
              }
              return ticketValueStr.includes(String(fv).toLowerCase());
            });
          }
          // Handle single value filter
          if (filterValue === EMPTY_VALUE_MARKER) {
            return ticketValue === undefined || ticketValue === null || ticketValue === '';
          }
          return ticketValueStr.includes(String(filterValue).toLowerCase());
        });
      }
    });
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(ticket => {
        return Object.values(ticket).some(val => 
          val && String(val).toLowerCase().includes(term)
        );
      });
    }
    
    return result;
  }, [tickets, statusFilter, columnFilters, searchTerm]);
  
  // Clear all column filters
  const clearAllFilters = () => {
    setColumnFilters({});
    setStatusFilter('all');
    setBilletFilter('all');
    setRecapMatchFilter(null);
    setCurrentPage(1);
  };
  
  // Check if any filter is active (including search term)
  const hasActiveFilters = useMemo(() => {
    const hasColumnFilter = Object.values(columnFilters).some(v => 
      v !== undefined && (Array.isArray(v) ? v.length > 0 : true)
    );
    return statusFilter !== 'all' || billetFilter !== 'all' || hasColumnFilter || !!searchTerm || !!recapMatchFilter;
  }, [statusFilter, billetFilter, columnFilters, searchTerm, recapMatchFilter]);
  
  // Toggle column visibility
  const toggleColumnVisibility = (columnKey) => {
    setHiddenColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };
  
  const columns = useMemo(() => {
    switch (viewType) {
      case 'achat':
        return getAchatColumns(project);
      case 'vente':
        return getVenteColumns(project);
      default:
        return getToutColumns(project);
    }
  }, [viewType, project]);
  
  // Get visible columns (must be after columns declaration)
  const visibleColumns = useMemo(() => {
    return columns.filter(col => !hiddenColumns.has(col.key));
  }, [columns, hiddenColumns]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const filteredAndSortedTickets = useMemo(() => {
    let result = billetFilter === 'all' ? [...tickets] : tickets.filter(t => matchBilletFilter(t, billetFilter));

    // Apply status filter FIRST
    if (statusFilter !== 'all') {
      result = result.filter(ticket => {
        const isVente = ticket.type === 'VENTE';
        const isAttribue = isVente 
          ? ticket.attribue === true
          : (ticket.attribue === true || (ticket.ref_vente && ticket.ref_vente.trim() !== ''));
        const isValide = ticket.statut === 'VALIDE';
        const isSpecial = ticket.statut === 'SPECIAL_MATCH';
        
        switch (statusFilter) {
          case 'attribue':
            return isAttribue && !isValide && !isSpecial;
          case 'non_attribue':
            return !isAttribue && !isValide;
          case 'valide':
            return isValide;
          case 'special':
            return isSpecial;
          default:
            return true;
        }
      });
    }
    
    // Special marker for empty values in filters
    const EMPTY_VALUE_MARKER = '__VIDE__';
    
    // Apply column filters (supports multi-select arrays)
    Object.entries(columnFilters).forEach(([columnKey, filterValue]) => {
      if (filterValue !== undefined && filterValue !== null) {
        // Skip empty arrays (no filter selected)
        if (Array.isArray(filterValue) && filterValue.length === 0) {
          return; // Skip this filter
        }
        
        result = result.filter(ticket => {
          const ticketValue = ticket[columnKey];
          const ticketValueStr = String(ticketValue || '').toLowerCase();
          
          // Multi-select: filterValue is an array
          if (Array.isArray(filterValue)) {
            return filterValue.some(fv => {
              // Handle "(Vide)" filter - match empty, null, or undefined values
              if (fv === EMPTY_VALUE_MARKER) {
                return ticketValue === undefined || ticketValue === null || ticketValue === '';
              }
              return ticketValueStr.includes(String(fv).toLowerCase());
            });
          }
          
          // Single value (backward compatibility)
          if (filterValue === EMPTY_VALUE_MARKER) {
            return ticketValue === undefined || ticketValue === null || ticketValue === '';
          }
          return ticketValueStr.includes(String(filterValue).toLowerCase());
        });
      }
    });

    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      
      // Special handling for match number search (M + number with 1-3 digits only)
      // Long numbers like "635867081" should search in ref_vente, not as match numbers
      const matchNumberSearch = term.match(/^m(\d{1,3})$/i) || (term.match(/^(\d{1,3})$/) && term.length <= 3);
      
      result = result.filter(ticket => {
        // If searching for a match number (like "M76" or "76" with max 3 digits), search in event name
        if (matchNumberSearch) {
          const searchNum = matchNumberSearch[1] || term;
          const eventName = ticket.evenement?.toLowerCase() || '';
          const matchPattern = new RegExp(`\\bm?${searchNum}\\b`, 'i');
          return matchPattern.test(eventName);
        }
        
        // Standard search: check all text fields including ref_vente
        const matchesEvenement = ticket.evenement?.toLowerCase().includes(term);
        const matchesRefAchat = ticket.ref_achat?.toLowerCase().includes(term);
        const matchesRefVente = ticket.ref_vente?.toLowerCase().includes(term);
        const matchesSource = ticket.source?.toLowerCase().includes(term);
        const matchesNomClient = ticket.nom_client?.toLowerCase().includes(term);
        const matchesMailClient = ticket.mail_client?.toLowerCase().includes(term);
        const matchesEmailCompte = ticket.email_compte?.toLowerCase().includes(term);
        const matchesLinkedMailCompte = ticket.linked_mail_du_compte?.toLowerCase().includes(term);
        const matchesLieu = ticket.lieu?.toLowerCase().includes(term);
        const matchesCategorie = ticket.categorie?.toLowerCase().includes(term);
        const matchesVille = ticket.ville?.toLowerCase().includes(term);
        const matchesPays = ticket.pays?.toLowerCase().includes(term);
        const matchesNote = ticket.note?.toLowerCase().includes(term);
        const matchesStatut = ticket.statut?.toLowerCase().includes(term);
        
        // For ACHAT tickets that are NOT attributed:
        // Don't show them if the ONLY match is on ref_achat
        // (This prevents showing old tickets that happen to have ref_achat = some ref_vente)
        const isAchat = ticket.type === 'ACHAT';
        const isNotAttribue = ticket.attribue !== true && (!ticket.ref_vente || ticket.ref_vente.trim() === '');
        
        if (isAchat && isNotAttribue && matchesRefAchat) {
          // Only match on ref_achat for non-attributed ACHAT - exclude unless other fields also match
          const hasOtherMatch = matchesEvenement || matchesRefVente || matchesSource || 
                                matchesNomClient || matchesMailClient || matchesEmailCompte ||
                                matchesLieu || matchesCategorie || matchesVille || matchesPays;
          if (!hasOtherMatch) {
            return false; // Exclude this ticket
          }
        }
        
        return matchesEvenement || matchesRefAchat || matchesRefVente || 
               matchesSource || matchesNomClient || matchesMailClient || matchesEmailCompte ||
               matchesLinkedMailCompte || matchesLieu || matchesCategorie || matchesVille || 
               matchesPays || matchesNote || matchesStatut;
      });
    }
    
    // Apply recap match filter (when clicking on a match in the summary panel)
    if (recapMatchFilter) {
      result = result.filter(ticket => {
        const matchNumMatch = ticket.evenement?.match(/M(\d+)/i);
        const ticketMatchKey = matchNumMatch ? `M${matchNumMatch[1]}` : 'Autre';
        return ticketMatchKey === recapMatchFilter;
      });
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'ref') {
          aVal = a.type === 'ACHAT' ? a.ref_achat : a.ref_vente;
          bVal = b.type === 'ACHAT' ? b.ref_achat : b.ref_vente;
        }

        // Sort by match number for evenement
        if (sortConfig.key === 'evenement') {
          aVal = extractMatchNum(a.evenement);
          bVal = extractMatchNum(b.evenement);
        }

        // ✅ TRI PAR DATE - Convertir DD/MM/YY ou DD/MM/YYYY en date réelle
        if (sortConfig.key === 'date_event' || sortConfig.key === 'date_vente' || sortConfig.key === 'date_evenement' || sortConfig.key === 'date_operation') {
          const parseDate = (dateStr) => {
            if (!dateStr) return new Date(0); // Dates vides en premier
            const str = String(dateStr).trim();
            
            // Format DD/MM/YY ou DD/MM/YYYY
            const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
            if (match) {
              const day = parseInt(match[1], 10);
              const month = parseInt(match[2], 10) - 1; // Mois 0-indexed
              let year = parseInt(match[3], 10);
              // Convertir année 2 chiffres en 4 chiffres (25 -> 2025, 26 -> 2026)
              if (year < 100) {
                year = year < 50 ? 2000 + year : 1900 + year;
              }
              return new Date(year, month, day);
            }
            
            // Essayer ISO format YYYY-MM-DD
            const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (isoMatch) {
              return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
            }
            
            return new Date(0);
          };
          
          aVal = parseDate(aVal);
          bVal = parseDate(bVal);
          
          // Comparer les timestamps
          const aTime = aVal.getTime();
          const bTime = bVal.getTime();
          if (aTime < bTime) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aTime > bTime) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }

        // ✅ TRI NUMÉRIQUE pour prix et quantité
        if (sortConfig.key === 'prix' || sortConfig.key === 'qty' || sortConfig.key === 'total') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort by event match number
      result.sort((a, b) => extractMatchNum(a.evenement) - extractMatchNum(b.evenement));
    }

    return result;
  }, [tickets, searchTerm, sortConfig, statusFilter, columnFilters, recapMatchFilter, billetFilter]);

  // Track previous filtered tickets to avoid infinite loops
  const prevFilteredLengthRef = useRef(0);
  const prevFilteredIdsRef = useRef('');

  // Notify parent of filtered tickets for export functionality
  // FIXED: Use refs to prevent infinite loops
  React.useEffect(() => {
    try {
      if (onFilteredTicketsChange && filteredAndSortedTickets) {
        // Create a stable ID string to check if data actually changed
        const currentLength = filteredAndSortedTickets.length;
        const currentIds = filteredAndSortedTickets.slice(0, 10).map(t => t.id).join(',');
        
        // Only notify if the data actually changed
        if (currentLength !== prevFilteredLengthRef.current || currentIds !== prevFilteredIdsRef.current) {
          prevFilteredLengthRef.current = currentLength;
          prevFilteredIdsRef.current = currentIds;
          onFilteredTicketsChange(filteredAndSortedTickets);
        }
      }
    } catch (e) {
      console.error('Error notifying filtered tickets:', e);
    }
  }, [filteredAndSortedTickets]); // Remove onFilteredTicketsChange from deps to avoid loops

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedTickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTickets = useMemo(() => {
    return filteredAndSortedTickets.slice(startIndex, endIndex);
  }, [filteredAndSortedTickets, startIndex, endIndex]);

  // Reset page when search or items per page changes (but NOT during silent refresh)
  React.useEffect(() => {
    if (!isSilentRefresh) {
      setCurrentPage(1);
    }
  }, [searchTerm, itemsPerPage]); // Remove isSilentRefresh from deps to avoid loops
  
  // Reset page when column filters change
  const columnFiltersCount = Object.keys(columnFilters).length;
  React.useEffect(() => {
    setCurrentPage(1);
  }, [columnFiltersCount]);
  
  // Ajuster la page si elle dépasse le nombre total de pages (après suppression de tickets)
  React.useEffect(() => {
    // Only adjust if totalPages is valid and page is out of range
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [totalPages]); // Only depend on totalPages, not currentPage to avoid loops
  
  // Envoyer les tickets filtrés au parent pour l'export
  // TOTALEMENT DÉSACTIVÉ - l'export sera géré différemment
  const filteredTicketsRef = React.useRef([]);
  
  // Stocker les tickets filtrés dans la ref (sans callback au parent)
  React.useEffect(() => {
    filteredTicketsRef.current = filteredAndSortedTickets;
  }, [filteredAndSortedTickets]);

  const formatPrice = (value, devise = 'EUR') => {
    if (!value && value !== 0) return '-';
    const symbol = { EUR: '€', GBP: '£', USD: '$' }[devise || 'EUR'] || devise;
    return `${Number(value).toFixed(2)} ${symbol}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const str = String(dateStr).trim();
      
      // Si déjà au format JJ/MM/AA ou JJ/MM/AAAA, le retourner tel quel
      const frMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (frMatch) {
        const day = frMatch[1].padStart(2, '0');
        const month = frMatch[2].padStart(2, '0');
        const year = frMatch[3].length === 2 ? frMatch[3] : frMatch[3].slice(-2);
        return `${day}/${month}/${year}`;
      }
      
      // Si format ISO (YYYY-MM-DD ou avec heure), convertir en JJ/MM/AA
      const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const day = isoMatch[3];
        const month = isoMatch[2];
        const year = isoMatch[1].slice(-2);
        return `${day}/${month}/${year}`;
      }
      
      // Essayer de parser comme Date et formater
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
      }
      
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const getCellValue = (ticket, columnKey) => {
    switch (columnKey) {
      case 'type':
        return <TypeBadge type={ticket.type} />;
      case 'ref':
        return <span className="font-mono text-xs">{ticket.type === 'ACHAT' ? ticket.ref_achat : ticket.ref_vente || '-'}</span>;
      case 'ref_achat':
        return <span className="font-mono text-xs">{ticket.ref_achat || '-'}</span>;
      case 'ref_vente':
        return <span className="font-mono text-xs">{ticket.ref_vente || '-'}</span>;
      case 'ref_vente_attribuer':
        return <span className="font-mono text-xs">{ticket.ref_vente || '-'}</span>;
      case 'statut':
        return <StatusBadge status={ticket.statut} />;
      case 'evenement':
        return <span className="truncate max-w-[200px] block text-sm" title={ticket.evenement}>{formatEventName(ticket.evenement)}</span>;
      case 'date_evenement':
        return <span className="font-mono text-xs">{formatDate(ticket.date_evenement)}</span>;
      case 'date_operation':
        return <span className="font-mono text-xs">{formatDate(ticket.date_operation)}</span>;
      case 'date_achat':
        return <span className="font-mono text-xs text-zinc-300">{formatDate(ticket.date_achat)}</span>;
      case 'lieu':
        return <span className="truncate max-w-[120px] block text-xs" title={ticket.lieu}>{ticket.lieu || '-'}</span>;
      case 'plan':
        const stadium = findStadium(ticket.lieu, ticket.ville);
        if (stadium) {
          // Extract match number from event name
          const matchNumMatch = ticket.evenement?.match(/M(\d+)/i);
          const matchNum = matchNumMatch ? `M${matchNumMatch[1]}` : null;
          
          const matchInfo = {
            matchNum: matchNum,
            eventName: ticket.evenement,
            date: ticket.date_evenement,
            categorie: ticket.categorie
          };
          
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                if (onShowStadiumPlan) onShowStadiumPlan(stadium, matchInfo);
              }}
              className="h-6 px-2 text-xs border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/10"
            >
              <MapPin className="h-3 w-3 mr-1" />
              Voir
            </Button>
          );
        }
        return <span className="text-xs text-zinc-500">-</span>;
      case 'ville':
        return <span className="text-xs">{ticket.ville || '-'}</span>;
      case 'pays':
        return <span className="text-xs">{ticket.pays || '-'}</span>;
      case 'categorie':
        return <span className="text-xs">{ticket.categorie || '-'}</span>;
      case 'session':
        // Afficher Day/Evening avec un icône
        const sessionValue = ticket.session || '';
        const isEvening = sessionValue.toLowerCase().includes('evening') || sessionValue.toLowerCase().includes('night') || sessionValue.toLowerCase().includes('nuit');
        const isDay = sessionValue.toLowerCase().includes('day') || sessionValue.toLowerCase().includes('jour');
        if (isEvening) {
          return <span className="text-xs text-purple-400">🌙 Evening</span>;
        } else if (isDay) {
          return <span className="text-xs text-yellow-500">☀️ Day</span>;
        }
        return <span className="text-xs text-zinc-500">-</span>;
      case 'tribune':
        return <span className="text-xs text-cyan-400">{ticket.tribune || '-'}</span>;
      case 'acces':
        return <span className="text-xs text-orange-400">{ticket.acces || '-'}</span>;
      case 'escalier':
        return <span className="text-xs text-pink-400">{ticket.escalier || '-'}</span>;
      case 'block':
        return <span className="text-xs text-cyan-400">{ticket.block || '-'}</span>;
      case 'pdf':
        return <PdfCell ticket={ticket} />;
      case 'rang':
        return <span className="text-xs text-green-400">{ticket.rang || '-'}</span>;
      case 'place':
        return <span className="text-xs text-blue-400">{ticket.place || '-'}</span>;
      case 'qty':
        return <span className="font-mono text-center">{ticket.qty || 1}</span>;
      case 'prix':
        return <span className="font-mono text-xs">{formatPrice(ticket.prix, ticket.devise)}</span>;
      case 'source':
        return <span className="text-xs">{ticket.source || '-'}</span>;
      case 'nom_client':
        return <span className="truncate max-w-[100px] block text-xs" title={ticket.nom_client}>{ticket.nom_client || '-'}</span>;
      case 'mail_client':
        return <span className="truncate max-w-[120px] block text-xs" title={ticket.mail_client}>{ticket.mail_client || '-'}</span>;
      case 'email_compte':
        // Pour VENTE, afficher linked_mail_du_compte si disponible
        const emailCompteValue = ticket.email_compte || ticket.linked_mail_du_compte || '-';
        const isLinked = !ticket.email_compte && ticket.linked_mail_du_compte;
        return <span className={`truncate max-w-[140px] block text-xs ${isLinked ? 'text-blue-400' : ''}`} title={emailCompteValue}>{emailCompteValue}</span>;
      case 'pw_compte':
        return <span className="text-xs font-mono">{ticket.pw_compte || '-'}</span>;
      case 'linked_mail_du_compte':
        // Display linked ACHAT's mail_du_compte for VENTE tickets
        return <span className="truncate max-w-[150px] block text-xs text-blue-400" title={ticket.linked_mail_du_compte}>{ticket.linked_mail_du_compte || '-'}</span>;
      case 'linked_pw_du_compte':
        // Display linked ACHAT's pw_du_compte for VENTE tickets (masked)
        return <span className="text-xs font-mono text-blue-400">{ticket.linked_pw_du_compte || '-'}</span>;
      case 'preuve_envoi':
        // Pour les tickets ACHAT: bouton pour upload/voir la preuve d'envoi
        const hasPreuveEnvoi = ticket.preuve_envoi && ticket.preuve_envoi !== '';
        const isPreuveEnvoiUrl = hasPreuveEnvoi && (ticket.preuve_envoi.startsWith('http') || ticket.preuve_envoi.startsWith('/uploads'));
        
        if (isPreuveEnvoiUrl) {
          // Has uploaded proof - show "Voir" button
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenProofEnvoiModal) {
                  onOpenProofEnvoiModal(ticket);
                }
              }}
              className="h-6 text-xs px-2 bg-green-500/20 border-green-500/40 text-green-400 hover:bg-green-500/30"
            >
              ✓ Voir
            </Button>
          );
        } else if (hasPreuveEnvoi) {
          // Legacy text link (not uploaded)
          return (
            <a 
              href={ticket.preuve_envoi} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gold hover:underline text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              Voir
            </a>
          );
        }
        
        // No proof - show "Ajouter" button
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              if (onOpenProofEnvoiModal) {
                onOpenProofEnvoiModal(ticket);
              }
            }}
            className="h-6 text-xs px-2 bg-zinc-700/50 border-zinc-600 text-zinc-400 hover:bg-zinc-600"
          >
            📷 Ajouter
          </Button>
        );
      case 'preuve':
        // Colonne PREUVE unifiée - fonctionne pour ACHAT (preuve_envoi) et VENTE (preuve_url)
        if (ticket.type === 'ACHAT') {
          // ACHAT: utilise preuve_envoi
          const hasAchatPreuve = ticket.preuve_envoi && ticket.preuve_envoi !== '';
          const isAchatPreuveUrl = hasAchatPreuve && (ticket.preuve_envoi.startsWith('http') || ticket.preuve_envoi.startsWith('/uploads'));
          
          if (isAchatPreuveUrl) {
            return (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenProofEnvoiModal) {
                    onOpenProofEnvoiModal(ticket);
                  }
                }}
                className="h-6 text-xs px-2 bg-green-500/20 border-green-500/40 text-green-400 hover:bg-green-500/30"
              >
                ✓ Voir
              </Button>
            );
          } else if (hasAchatPreuve) {
            return (
              <a 
                href={ticket.preuve_envoi} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gold hover:underline text-xs"
                onClick={(e) => e.stopPropagation()}
              >
                Voir
              </a>
            );
          }
          
          // No proof - show "Ajouter" button for ACHAT
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenProofEnvoiModal) {
                  onOpenProofEnvoiModal(ticket);
                }
              }}
              className="h-6 text-xs px-2 bg-zinc-700/50 border-zinc-600 text-zinc-400 hover:bg-zinc-600"
            >
              📷 Ajouter
            </Button>
          );
        }
        
        // VENTE: utilise preuve_url
        const hasPreuve = ticket.preuve_url && ticket.preuve_url !== '';
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              if (onOpenProofModal) {
                onOpenProofModal(ticket);
              }
            }}
            className={`h-6 text-xs px-2 ${
              hasPreuve 
                ? 'bg-green-500/20 border-green-500/40 text-green-400 hover:bg-green-500/30' 
                : 'bg-zinc-700/50 border-zinc-600 text-zinc-400 hover:bg-zinc-600'
            }`}
          >
            {hasPreuve ? '✓ Voir' : '📷 Ajouter'}
          </Button>
        );
      case 'note':
        return <span className="truncate max-w-[100px] block text-xs" title={ticket.note}>{ticket.note || '-'}</span>;
      case 'ref_vente':
        // For TOUT view - show ref_vente for both ACHAT and VENTE
        const refValue = ticket.ref_vente || ticket.ref_vente_attribuer || '';
        return <span className="text-xs font-mono text-blue-400">{refValue || '-'}</span>;
      case 'attribuer':
        // Check if CANCELLED (ANNULEE status) - red button, highest priority
        const isCancelled = ticket.statut === 'ANNULEE';
        if (isCancelled) {
          return (
            <Button
              size="sm"
              variant="default"
              onClick={(e) => e.stopPropagation()}
              disabled
              className="h-6 text-xs px-2 bg-red-600 hover:bg-red-600 text-white font-semibold cursor-not-allowed"
            >
              ✗ ANNULÉ
            </Button>
          );
        }
        
        // Check if validated (VALIDE status) - green button
        const isValidated = ticket.statut === 'VALIDE';
        
        // Check if attributed based on view type
        // For VENTE: use attribue flag only (statut is secondary indicator)
        // For ACHAT: check attribue flag or ref_vente (attribution link)
        // In TOUT view: check ticket type to apply correct logic
        const isVenteTicket = ticket.type === 'VENTE';
        const isAttributed = isVenteTicket
          ? (ticket.attribue === true)  // VENTE: only check attribue flag
          : (ticket.attribue === true || 
             (ticket.ref_vente && ticket.ref_vente.trim() !== '') ||
             (ticket.ref_vente_attribuer && ticket.ref_vente_attribuer.trim() !== ''));  // ACHAT: check attribue or ref_vente
        
        // If VALIDATED - show green button for ALL views
        if (isValidated) {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="default"
                  onClick={(e) => e.stopPropagation()}
                  className="h-6 text-xs px-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                >
                  ✓ VALIDÉ
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem
                  onClick={() => onMarkAttribue?.(ticket.id, false)}
                  className="text-red-400"
                >
                  <X className="h-4 w-4 mr-2" />
                  Désattribuer (annuler validation)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }
        
        // For ACHAT view - only allow Désattribuer/Valider, NOT Attribuer
        if (viewType === 'achat') {
          // Check if this is a SPECIAL_MATCH (gold button)
          const isSpecialMatch = ticket.statut === 'SPECIAL_MATCH';
          
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant={isAttributed ? "default" : "outline"}
                  onClick={(e) => e.stopPropagation()}
                  className={cn(
                    "h-6 text-xs px-2",
                    isAttributed 
                      ? isSpecialMatch
                        ? "bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                        : "bg-blue-500 hover:bg-blue-600 text-white" 
                      : "border-zinc-500 text-zinc-400 hover:bg-zinc-700 bg-zinc-800"
                  )}
                >
                  {isAttributed ? (isSpecialMatch ? "Spécial" : "Attribué") : "Non attr."}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                {isAttributed ? (
                  <>
                    <DropdownMenuItem
                      onClick={() => onMarkAttribue?.(ticket.id, false)}
                      className="text-zinc-400"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Désattribuer
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onValidateTicket?.(ticket.id)}
                      className="text-emerald-400"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Valider ✓
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem disabled className="text-amber-400 text-xs">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Aller dans VENTE pour attribuer
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }
        
        // For VENTE view - show "Attribuer" which opens modal to select matching ACHAT
        if (viewType === 'vente') {
          // Check if this is a SPECIAL_MATCH (gold button)
          const isSpecialMatch = ticket.statut === 'SPECIAL_MATCH';
          
          // If attributed but not validated, show dropdown with validate option
          if (isAttributed) {
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={(e) => e.stopPropagation()}
                    className={cn(
                      "h-6 text-xs px-2",
                      isSpecialMatch 
                        ? "bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                        : "bg-blue-500 hover:bg-blue-600 text-white"
                    )}
                  >
                    {isSpecialMatch ? "✓ Spécial" : "✓ Attribué"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-48">
                  <DropdownMenuItem
                    onClick={() => onMarkAttribue?.(ticket.id, false)}
                    className="text-zinc-400"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Désattribuer
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onValidateTicket?.(ticket.id)}
                    className="text-emerald-400"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Valider ✓
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            );
          }
          
          // Not attributed - show "Attribuer" button
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                if (onAttribuerVente) {
                  onAttribuerVente(ticket);
                }
              }}
              className={cn(
                "h-6 text-xs px-2",
                isAttributed 
                  ? "bg-emerald-500 hover:bg-emerald-600 text-white" 
                  : "border-[#C4A052] text-[#C4A052] hover:bg-[#C4A052]/20 bg-zinc-800"
              )}
            >
              {isAttributed ? "✓ Attribué" : "Attribuer →"}
            </Button>
          );
        }
        
        // For TOUT view - ACHAT tickets should also show "go to VENTE" message
        // Check if this is an ACHAT ticket
        const isAchatTicket = ticket.type === 'ACHAT';
        
        if (isAchatTicket) {
          // Same behavior as ACHAT view for ACHAT tickets in TOUT view
          // Check if this is a SPECIAL_MATCH (gold button)
          const isSpecialMatch = ticket.statut === 'SPECIAL_MATCH';
          
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant={isAttributed ? "default" : "outline"}
                  onClick={(e) => e.stopPropagation()}
                  className={cn(
                    "h-6 text-xs px-2",
                    isAttributed 
                      ? isSpecialMatch
                        ? "bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                        : "bg-blue-500 hover:bg-blue-600 text-white" 
                      : "border-zinc-500 text-zinc-400 hover:bg-zinc-700 bg-zinc-800"
                  )}
                >
                  {isAttributed ? (isSpecialMatch ? "Spécial" : "Attribué") : "Non attr."}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                {isAttributed ? (
                  <>
                    <DropdownMenuItem
                      onClick={() => onMarkAttribue?.(ticket.id, false)}
                      className="text-zinc-400"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Désattribuer
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onValidateTicket?.(ticket.id)}
                      className="text-emerald-400"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Valider ✓
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem disabled className="text-amber-400 text-xs">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Aller dans VENTE pour attribuer
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }
        
        // For VENTE tickets in TOUT view
        // Check if this is a SPECIAL_MATCH (gold button)
        const isSpecialMatchVente = ticket.statut === 'SPECIAL_MATCH';
        
        if (isAttributed) {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="default"
                  onClick={(e) => e.stopPropagation()}
                  className={cn(
                    "h-6 text-xs px-2",
                    isSpecialMatchVente 
                      ? "bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                      : "bg-blue-500 hover:bg-blue-600 text-white"
                  )}
                >
                  {isSpecialMatchVente ? "✓ Spécial" : "✓ Attribué"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem
                  onClick={() => onMarkAttribue?.(ticket.id, false)}
                  className="text-zinc-400"
                >
                  <X className="h-4 w-4 mr-2" />
                  Désattribuer
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onValidateTicket?.(ticket.id)}
                  className="text-emerald-400"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Valider ✓
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }
        
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              if (onAttribuerVente) {
                onAttribuerVente(ticket);
              }
            }}
            className="h-6 text-xs px-2 border-[#C4A052] text-[#C4A052] hover:bg-[#C4A052]/20 bg-zinc-800"
          >
            Attribuer →
          </Button>
        );
      default:
        return ticket[columnKey] || '-';
    }
  };

  // Selection functions - only for current page
  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedTickets.length && paginatedTickets.every(t => selectedIds.has(t.id))) {
      // Deselect current page
      const newSelected = new Set(selectedIds);
      paginatedTickets.forEach(t => newSelected.delete(t.id));
      setSelectedIds(newSelected);
    } else {
      // Select current page
      const newSelected = new Set(selectedIds);
      paginatedTickets.forEach(t => newSelected.add(t.id));
      setSelectedIds(newSelected);
    }
  };

  const selectAllFiltered = () => {
    if (selectedIds.size === filteredAndSortedTickets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAndSortedTickets.map(t => t.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    if (window.confirm(`Supprimer ${selectedIds.size} billet(s) sélectionné(s) ?`)) {
      onBulkDelete?.(Array.from(selectedIds));
      setSelectedIds(new Set());
    }
  };

  // Bulk désattribuer function
  const handleBulkDesattribuer = async () => {
    if (selectedIds.size === 0) return;
    
    const selectedTickets = tickets.filter(t => selectedIds.has(t.id));
    const attribuesCount = selectedTickets.filter(t => t.attribue || t.ref_vente).length;
    
    if (attribuesCount === 0) {
      alert('Aucun billet attribué dans la sélection');
      return;
    }
    
    if (!window.confirm(`Désattribuer ${attribuesCount} billet(s) attribué(s) sur ${selectedIds.size} sélectionné(s) ?\n\nCela va:\n- Effacer ref_vente, nom_client, mail_client\n- Remettre le statut à vide\n- Libérer les billets pour une nouvelle attribution`)) {
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      let successCount = 0;
      let errorCount = 0;
      
      // Désattribuer chaque ticket sélectionné qui est attribué
      for (const ticket of selectedTickets) {
        if (ticket.attribue || ticket.ref_vente) {
          try {
            const response = await fetch(`${API_URL}/api/tickets/${ticket.id}/attribue?attribue=false`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            });
            
            if (response.ok) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (e) {
            errorCount++;
          }
        }
      }
      
      // Clear selection
      setSelectedIds(new Set());
      
      // Show result
      if (errorCount === 0) {
        alert(`✅ ${successCount} billet(s) désattribué(s) avec succès`);
      } else {
        alert(`⚠️ ${successCount} réussi(s), ${errorCount} erreur(s)`);
      }
      
      // Trigger refresh via window reload or callback
      window.location.reload();
    } catch (error) {
      console.error('Bulk desattribuer error:', error);
      alert(`Erreur: ${error.message}`);
    }
  };

  // Copier la sélection dans le presse-papier (format tableur)
  const handleCopySelection = () => {
    if (selectedIds.size === 0) return;
    
    const selectedTickets = tickets.filter(t => selectedIds.has(t.id));
    
    // En-têtes
    const headers = [
      'REF_VENTE', 'DATE', 'SOURCE', 'MATCH', 'LIEU', 'VILLE', 'PAYS', 
      'DATE_MATCH', 'CATEGORIE', 'QTY', 'PRIX', 'NOM_CLIENT', 'MAIL_CLIENT',
      'EMAIL_COMPTE', 'BLOCK', 'RANG', 'PLACE', 'NOTE', 'STATUT'
    ];
    
    // Données
    const rows = selectedTickets.map(t => [
      t.ref_vente || '',
      t.date_operation || '',
      t.source || '',
      t.evenement || '',
      t.lieu || '',
      t.ville || '',
      t.pays || '',
      t.date_evenement || '',
      t.categorie || '',
      t.qty || '',
      t.prix || '',
      t.nom_client || '',
      t.mail_client || '',
      t.email_compte || '',
      t.block || '',
      t.rang || '',
      t.place || '',
      t.note || '',
      t.statut || ''
    ].join('\t')).join('\n');
    
    const clipboardText = headers.join('\t') + '\n' + rows;
    
    navigator.clipboard.writeText(clipboardText).then(() => {
      alert(`✅ ${selectedTickets.length} ligne(s) copiée(s) !\n\nCollez dans Google Sheets ou Excel.`);
    }).catch(err => {
      console.error('Erreur copie:', err);
      alert('Erreur lors de la copie');
    });
  };

  // Bulk edit function
  const handleBulkEdit = async () => {
    if (selectedIds.size === 0 || !bulkEditField || bulkEditValue === '') return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/tickets/bulk-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_ids: Array.from(selectedIds),
          field: bulkEditField,
          value: bulkEditValue,
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Bulk update error response:', errorText);
        throw new Error(`Erreur serveur: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Close modal and reset
      setBulkEditOpen(false);
      setBulkEditField('');
      setBulkEditValue('');
      setSelectedIds(new Set());
      
      alert(`✅ ${result.updated} billet(s) modifié(s) avec succès !`);
      
      // Reload the page to see changes
      window.location.reload();
    } catch (error) {
      console.error('Bulk edit error:', error);
      alert(`❌ Erreur lors de la modification en masse: ${error.message}`);
    }
  };

  const isPageSelected = paginatedTickets.length > 0 && paginatedTickets.every(t => selectedIds.has(t.id));

  return (
    <div className="space-y-4">
      {/* Search and Bulk Actions */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="ticket-search-input"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-background border-gold/20 focus:border-gold/50"
            />
          </div>
          {/* Clear filters button - show when filters are active */}
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
            >
              <X className="h-4 w-4 mr-1" />
              Réinitialiser filtres
            </Button>
          )}
          {/* Items per page selector */}
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="bg-zinc-800 border border-zinc-600 text-white text-sm rounded px-2 py-1.5 focus:border-gold/50 outline-none"
          >
            {PAGE_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt} / page</option>
            ))}
          </select>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {filteredAndSortedTickets.length} lignes
            {stats && viewType === 'tout' && stats.total_tickets > filteredAndSortedTickets.length && (
              <span className="text-zinc-500"> / {stats.total_tickets} total</span>
            )}
            {stats && viewType === 'achat' && stats.total_achats > filteredAndSortedTickets.length && (
              <span className="text-zinc-500"> / {stats.total_achats} total</span>
            )}
            {stats && viewType === 'vente' && stats.total_ventes > filteredAndSortedTickets.length && (
              <span className="text-zinc-500"> / {stats.total_ventes} total</span>
            )}
          </span>
        </div>
        
        {/* Column Visibility & Bulk Actions */}
        <div className="flex items-center gap-2">
          {/* Column Visibility Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
              >
                <Eye className="h-4 w-4 mr-2" />
                Colonnes
                {hiddenColumns.size > 0 && (
                  <span className="ml-1 text-[10px] bg-[#C4A052] text-black rounded-full px-1 font-bold">
                    {columns.length - hiddenColumns.size}/{columns.length}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
              <DropdownMenuItem 
                onSelect={(e) => {
                  e.preventDefault();
                  setHiddenColumns(new Set());
                }}
                className={cn(!hiddenColumns.size && "bg-zinc-700")}
              >
                <Eye className="h-4 w-4 mr-2" />
                <span>Afficher toutes</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {columns.filter(col => col.key !== 'select' && col.key !== 'actions').map((col) => {
                const isVisible = !hiddenColumns.has(col.key);
                return (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible}
                    onCheckedChange={() => toggleColumnVisibility(col.key)}
                    onSelect={(e) => e.preventDefault()}
                    className="text-xs"
                  >
                    {col.label || col.key}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Bulk Actions */}
          <Button
            variant="outline"
            size="sm"
            onClick={selectAllFiltered}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
          >
            <CheckSquare className="h-4 w-4 mr-2" />
            Tout ({filteredAndSortedTickets.length})
          </Button>
          {selectedIds.size > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
                className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
              >
                <Square className="h-4 w-4 mr-2" />
                Désélect.
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkEditOpen(true)}
                className="border-[#C4A052] text-[#C4A052] hover:bg-[#C4A052]/20"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Modifier ({selectedIds.size})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopySelection}
                className="border-blue-500 text-blue-500 hover:bg-blue-500/20"
                data-testid="bulk-copy-button"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copier ({selectedIds.size})
              </Button>
              <BulkPdfButton tickets={filteredAndSortedTickets.filter(t => selectedIds.has(t.id))} />
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkDesattribuer}
                className="border-orange-500 text-orange-500 hover:bg-orange-500/20"
                data-testid="bulk-desattribuer-button"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Désattribuer ({selectedIds.size})
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer ({selectedIds.size})
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
          >
            ««
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
          >
            ‹ Préc
          </Button>
          <span className="text-sm text-white px-4">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
          >
            Suiv ›
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
          >
            »»
          </Button>
        </div>
      )}

      {/* PANNEAU RÉCAPITULATIF - Affiché quand un filtre est actif */}
      <FilterSummaryPanel 
        tickets={filteredAndSortedTickets} 
        hasFilter={hasActiveFilters || searchTerm.length > 0}
        searchTerm={searchTerm}
        onMatchClick={(matchNum) => {
          setRecapMatchFilter(matchNum);
          setCurrentPage(1);
        }}
        selectedMatchFilter={recapMatchFilter}
        onClearMatchFilter={() => {
          setRecapMatchFilter(null);
          setCurrentPage(1);
        }}
      />

      {/* Table */}
      <div className="table-container overflow-x-auto scrollbar-thin rounded-lg border border-gold/20">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-gold/20">
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(
                    'bg-card/80 backdrop-blur-sm sticky top-0 select-none',
                    'text-xs uppercase tracking-wide font-bold text-white',
                    col.key === 'actions' || col.key === 'select' ? 'cursor-default' : ''
                  )}
                  style={{ width: col.width, minWidth: col.width }}
                >
                  {col.key === 'select' ? (
                    <Checkbox
                      checked={isPageSelected}
                      onCheckedChange={toggleSelectAll}
                      className="border-zinc-500"
                    />
                  ) : col.key === 'actions' ? (
                    <span></span>
                  ) : col.key === 'pdf' ? (
                    <BilletFilterHeader value={billetFilter} onChange={(v) => { setBilletFilter(v); setCurrentPage(1); }} />
                  ) : col.key === 'attribuer' ? (
                    // Status filter dropdown for Attribuer column
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="flex items-center gap-1 hover:text-[#C4A052] transition-colors">
                          <span className={cn(
                            statusFilter !== 'all' && "text-[#C4A052]"
                          )}>
                            {statusFilter === 'all' ? 'Statut' : 
                             statusFilter === 'attribue' ? 'Attribué' :
                             statusFilter === 'non_attribue' ? 'Non attr.' :
                             statusFilter === 'valide' ? 'Validé' :
                             statusFilter === 'special' ? 'Spécial' : 'Statut'}
                          </span>
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" className="w-40">
                        <DropdownMenuItem 
                          onClick={() => setStatusFilter('all')}
                          className={cn(statusFilter === 'all' && "bg-zinc-700")}
                        >
                          <span className="text-zinc-300">Tous</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setStatusFilter('non_attribue')}
                          className={cn(statusFilter === 'non_attribue' && "bg-zinc-700")}
                        >
                          <span className="text-zinc-400">Non attribué</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setStatusFilter('attribue')}
                          className={cn(statusFilter === 'attribue' && "bg-zinc-700")}
                        >
                          <span className="text-blue-400">Attribué</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setStatusFilter('valide')}
                          className={cn(statusFilter === 'valide' && "bg-zinc-700")}
                        >
                          <span className="text-emerald-400">Validé</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setStatusFilter('special')}
                          className={cn(statusFilter === 'special' && "bg-zinc-700")}
                        >
                          <span className="text-amber-400">Spécial</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    // Regular column with sort + filter
                    <div className="flex items-center gap-1">
                      {/* Sort on click */}
                      <button 
                        onClick={() => handleSort(col.key)}
                        className="flex items-center hover:text-[#C4A052] transition-colors"
                      >
                        <span className={cn(
                          columnFilters[col.key] && "text-[#C4A052]"
                        )}>
                          {col.label}
                        </span>
                        <SortIcon columnKey={col.key} sortConfig={sortConfig} />
                      </button>
                      
                      {/* Filter dropdown - Uses backend values for complete list */}
                      <ColumnFilterDropdown
                        columnKey={col.key}
                        tickets={tickets}
                        columnFilters={columnFilters}
                        otherFiltersTickets={getTicketsFilteredExcept(col.key)}
                        allFilterValues={allFilterValues}
                        onFilterChange={(key, value) => {
                          setColumnFilters(prev => ({
                            ...prev,
                            [key]: value
                          }));
                        }}
                      />
                    </div>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="text-center py-12">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                    <span className="text-muted-foreground">Chargement...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredAndSortedTickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="text-center py-12">
                  <p className="text-muted-foreground">Aucun billet trouvé</p>
                </TableCell>
              </TableRow>
            ) : (
              paginatedTickets.map((ticket) => {
                // Determine row status for CSS targeting
                const normalizedStatus = ticket.statut?.toUpperCase?.() || '';
                const ticketType = ticket.type?.toUpperCase?.() || '';
                let rowStatus = 'default';
                
                if (normalizedStatus === 'VALIDE') {
                    rowStatus = 'valide';
                } else if (normalizedStatus === 'SPECIAL_MATCH') {
                    rowStatus = 'special';
                } else if (normalizedStatus === 'ANNULEE' || normalizedStatus === 'ANNULÉ') {
                    rowStatus = 'annulee';
                } else if (ticketType === 'VENTE' && ticket.attribue) {
                    rowStatus = 'attribue';
                } else if (ticketType === 'ACHAT' && ticket.ref_vente?.trim()) {
                    rowStatus = 'attribue';
                }
                
                return (
                <TableRow
                  key={ticket.id}
                  data-testid={`ticket-row-${ticket.id}`}
                  data-row-status={rowStatus}
                  className={cn(
                    'transition-colors border-b border-gold/10 hover:bg-gold/5',
                    // Only apply Tailwind background classes in dark mode
                    // In light mode, CSS rules in App.css will handle row colors via data-row-status
                    !isLightMode && getRowStatusClass(ticket.statut, ticket),
                    // Selected row: gold background + gold border (ring)
                    selectedIds.has(ticket.id) && 'bg-gold/15 ring-2 ring-[#C4A052] ring-inset'
                  )}
                >
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key} className="py-1 px-2">
                      {col.key === 'select' ? (
                        <Checkbox
                          checked={selectedIds.has(ticket.id)}
                          onCheckedChange={() => toggleSelect(ticket.id)}
                          className="border-zinc-500"
                        />
                      ) : col.key === 'actions' ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-gold/10"
                              data-testid={`ticket-actions-${ticket.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            {ticket.statut === 'MATCH_OK' && (
                              <DropdownMenuItem
                                data-testid={`unmatch-ticket-${ticket.id}`}
                                onClick={() => onUnmatch?.(ticket.id)}
                              >
                                <Link2Off className="h-4 w-4 mr-2" />
                                Unmatch
                              </DropdownMenuItem>
                            )}
                            {!ticket.statut && (
                              <DropdownMenuItem
                                onClick={() => onUpdateStatus?.(ticket.id, 'VALIDE')}
                              >
                                <Check className="h-4 w-4 mr-2 text-emerald-400" />
                                Valider
                              </DropdownMenuItem>
                            )}
                            {ticket.statut !== 'ANNULEE' && (
                              <DropdownMenuItem
                                onClick={() => onUpdateStatus?.(ticket.id, 'ANNULEE')}
                              >
                                <X className="h-4 w-4 mr-2 text-red-400" />
                                Annuler
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => onEdit?.(ticket)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`delete-ticket-${ticket.id}`}
                              onClick={() => onDelete?.(ticket.id)}
                              className="text-red-400 focus:text-red-400"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        getCellValue(ticket, col.key)
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )})
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination en bas */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2 py-3 border-t border-gold/10">
          <div className="flex items-center gap-2">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-zinc-800 border border-zinc-600 text-white text-sm rounded px-2 py-1.5 focus:border-gold/50 outline-none"
            >
              {PAGE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt} / page</option>
              ))}
            </select>
            <span className="text-sm text-muted-foreground">
              {startIndex + 1}-{Math.min(endIndex, filteredAndSortedTickets.length)} sur {filteredAndSortedTickets.length}
            </span>
          </div>
          
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 px-2"
            >
              ««
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 px-2"
            >
              ‹
            </Button>
            <span className="px-3 text-sm text-white">
              Page {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 px-2"
            >
              ›
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 px-2"
            >
              »»
            </Button>
          </div>
        </div>
      )}
      
      {/* Stadium Plan Modal */}
      <StadiumPlanModal
        open={stadiumPlanOpen}
        onOpenChange={setStadiumPlanOpen}
        stadium={selectedStadium}
        matchInfo={selectedMatchInfo}
      />
      
      {/* Bulk Edit Modal */}
      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-[#C4A052]/30">
          <DialogHeader>
            <DialogTitle className="text-[#C4A052] flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Modifier la sélection
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Modifier {selectedIds.size} billet(s) sélectionné(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Field selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300">
                Quel champ souhaitez-vous modifier ?
              </label>
              <Select value={bulkEditField} onValueChange={setBulkEditField}>
                <SelectTrigger className="w-full bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Sélectionner un champ..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {BULK_EDIT_FIELDS.map((field) => (
                    <SelectItem 
                      key={field.key} 
                      value={field.key}
                      className="text-white hover:bg-zinc-700 focus:bg-zinc-700"
                    >
                      {field.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Value input */}
            {bulkEditField && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-300">
                  Nouvelle valeur pour "{BULK_EDIT_FIELDS.find(f => f.key === bulkEditField)?.label}"
                </label>
                <Input
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder="Entrez la nouvelle valeur..."
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setBulkEditOpen(false);
                setBulkEditField('');
                setBulkEditValue('');
              }}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
            >
              Annuler
            </Button>
            <Button
              onClick={handleBulkEdit}
              disabled={!bulkEditField || bulkEditValue === ''}
              className="bg-[#C4A052] hover:bg-[#D4B86A] text-black font-semibold disabled:opacity-50"
            >
              <Check className="h-4 w-4 mr-2" />
              Appliquer à {selectedIds.size} billet(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketTable;
